import type { ProductBrandType, ProductType } from "@1js/pl-card-components";
import { getOcid, ProductBranding } from "@1js/pl-card-components";
import type { Prompt } from "@1js/pl-types";
import { useMsal } from "@azure/msal-react";
import React from "react";
import { MetaOSAppContext } from "../pages/metaos/MetaOSAppContext";
import { handoffToM365Chat } from "../services/handoffToM365Chat";
import {
  createPromptLink,
  sendSharedPromptEmailNotification,
  type PostSendSharePromptEmailNotificationRequestModel,
  type PostSendSharePromptEmailNotificationResponse,
} from "../services/prompt-client.web";
import { LogCategory, LogType } from "../utils/Telemetry/events";
import useTelemetry from "../utils/Telemetry/useTelemetry";
import { useFlightContext } from "./useFlightContext";

export type UseNotificationProps = {
  host: string;
  facets: Record<string, string[]>;
};

export type UseNotificationReturn = {
  sendWorkgroupNotification: (
    prompts: Prompt[],
    groupIds: string[]
  ) => PostSendSharePromptEmailNotificationResponse;
};

export const useNotifications = ({ host, facets }: UseNotificationProps) => {
  const { sendApiActionEvent, sendDiagnosticEvent } = useTelemetry();
  const metaOSAppContext = React.useContext(MetaOSAppContext);
  const msalInstance = useMsal();
  const instance =
    metaOSAppContext.isMosApp && metaOSAppContext.pca
      ? msalInstance.instance
      : undefined;
  const { isFlightActive } = useFlightContext();

  const sendWorkgroupNotification = React.useCallback(
    async (prompts: Prompt[], groupIds: string[]) => {
      if (!isFlightActive("isWorkgroupNotificationEnabled")) {
        return new Map<string, string>();
      }

      for (const prompt of prompts) {
        try {
          // We use this to fetch prompt data on SupportService, it cannot be managed.
          const promptLinkToFetchData = await createPromptLink(
            host,
            prompt,
            sendApiActionEvent,
            instance,
            "notificationFetch"
          );

          // We use this in the email notification, it can be managed.
          const promptLinkForSharing = await createPromptLink(
            host,
            prompt,
            sendApiActionEvent,
            instance,
            "notificationShare"
          );

          const productFacets = facets["products/name"];
          let preferredProduct: ProductType | undefined = undefined;
          if (productFacets) {
            preferredProduct = prompt.Products.find(
              (product) => productFacets.indexOf(product) !== -1
            ) as ProductType | undefined;
          }

          const product =
            preferredProduct ?? (prompt.Products[0] as ProductType);

          const isCreateTryInUrl = product === "BizChat" || product === "CWC";
          const isCreateGalleryUrl = !isCreateTryInUrl;
          const ocid = getOcid("notificationShare", "CopilotLab");

          let galleryUrl: URL | undefined;

          if (isCreateGalleryUrl) {
            galleryUrl = new URL(`https://${host}/prompts/link`);
            galleryUrl.searchParams.append(
              "LinkText",
              encodeURIComponent(promptLinkForSharing.PromptEncodedLink)
            );
            galleryUrl.searchParams.append("Product", product);
            galleryUrl.searchParams.append("ocid", ocid);
          }

          let bizChatHandoffUrl: URL | undefined = undefined;
          if (isCreateTryInUrl) {
            const url = (
              await handoffToM365Chat(
                prompt,
                "M365Chat",
                isFlightActive("isTryInAppendM365ChatFeatures"),
                isFlightActive("isTryIn1_1FormatEnabled"),
                isFlightActive("isTryIn3SPromptLinkEnabled"),
                sendApiActionEvent,
                instance,
                "work",
                isFlightActive("isBizChatTryInTeamsAppEnabled"),
                "notificationShare"
              )
            ).handoffUrl;
            bizChatHandoffUrl = new URL(url);
            bizChatHandoffUrl.searchParams.append("ocid", ocid);
          }

          const copilotPromptGalleryBrand: ProductBrandType =
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            ProductBranding["Default"];
          const productBrand: ProductBrandType =
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            ProductBranding[product] || ProductBranding["Default"];

          const model: PostSendSharePromptEmailNotificationRequestModel = {
            Workgroups: groupIds.map((groupId) => {
              return {
                GroupId: groupId,
                TryInUrl: bizChatHandoffUrl?.toString() || "",
                CopilotGalleryUrl: galleryUrl?.toString() || "",
              };
            }),
            Product: product,
            PrimaryColor:
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              productBrand.primaryBackgroundColor ??
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              copilotPromptGalleryBrand.primaryBackgroundColor,
            PrimaryTextColor:
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              productBrand.primaryTextColor ??
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              copilotPromptGalleryBrand.primaryTextColor,
            PrimaryBorderColor:
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              productBrand.primaryBorderColor ??
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              copilotPromptGalleryBrand.primaryBorderColor,
            SecondaryColor:
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              productBrand.secondaryBackgroundColor ??
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              copilotPromptGalleryBrand.secondaryBackgroundColor ??
              "",
            SecondaryTextColor:
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              productBrand.secondaryTextColor ??
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              copilotPromptGalleryBrand.secondaryTextColor ??
              "",
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            SecondaryBorderColor:
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              productBrand.secondaryBorderColor ??
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              copilotPromptGalleryBrand.secondaryBorderColor ??
              "",
            PromptLink: promptLinkToFetchData.PromptEncodedLink,
          };

          return await sendSharedPromptEmailNotification(
            host,
            model,
            sendApiActionEvent,
            instance
          );
        } catch (error) {
          sendDiagnosticEvent(
            LogType.Error,
            `An error occurred while sending notification.`,
            LogCategory.NOTIFICATION
          );
        }
      }
    },
    [
      facets,
      host,
      instance,
      isFlightActive,
      sendApiActionEvent,
      sendDiagnosticEvent,
    ]
  );

  return {
    sendWorkgroupNotification,
  };
};
